import React from 'react';
import { Box, Card, CardContent, Typography, Skeleton, alpha } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: React.ReactNode;
  trend?: 'up' | 'down' | 'flat';
  trendValue?: string;
  color?: string;
  loading?: boolean;
}

export function MetricCard({
  title,
  value,
  subtitle,
  icon,
  trend,
  trendValue,
  color = '#EC4899',
  loading = false,
}: MetricCardProps) {
  const getTrendIcon = () => {
    switch (trend) {
      case 'up':
        return <TrendingUpIcon sx={{ color: '#22C55E', fontSize: 18 }} />;
      case 'down':
        return <TrendingDownIcon sx={{ color: '#EF4444', fontSize: 18 }} />;
      case 'flat':
        return <TrendingFlatIcon sx={{ color: '#F59E0B', fontSize: 18 }} />;
      default:
        return null;
    }
  };

  const getTrendColor = () => {
    switch (trend) {
      case 'up':
        return '#22C55E';
      case 'down':
        return '#EF4444';
      default:
        return '#6B7280';
    }
  };

  if (loading) {
    return (
      <Card
        sx={{
          height: '100%',
          borderRadius: '16px',
          border: '1px solid #E5E7EB',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Skeleton width="60%" height={20} sx={{ borderRadius: '4px' }} />
          <Skeleton width="80%" height={40} sx={{ mt: 1.5, borderRadius: '4px' }} />
          <Skeleton width="40%" height={16} sx={{ mt: 1, borderRadius: '4px' }} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: '16px',
        border: '1px solid #E5E7EB',
        bgcolor: '#FFFFFF',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.08)',
          borderColor: alpha(color, 0.3),
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Typography
            variant="body2"
            sx={{
              color: '#6B7280',
              fontWeight: 600,
              fontSize: '0.875rem',
            }}
          >
            {title}
          </Typography>
          {icon && (
            <Box
              sx={{
                p: 1,
                borderRadius: '10px',
                bgcolor: alpha(color, 0.1),
                color: color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '& svg': {
                  fontSize: 20,
                },
              }}
            >
              {icon}
            </Box>
          )}
        </Box>

        <Typography
          variant="h4"
          sx={{
            mt: 1.5,
            fontWeight: 700,
            color: '#111827',
            fontSize: '1.75rem',
          }}
        >
          {value}
        </Typography>

        {(trend || subtitle) && (
          <Box display="flex" alignItems="center" gap={0.5} mt={1.5}>
            {trend && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 0.25,
                  borderRadius: '6px',
                  bgcolor: alpha(getTrendColor(), 0.1),
                }}
              >
                {getTrendIcon()}
                {trendValue && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: getTrendColor(),
                      fontWeight: 600,
                      fontSize: '0.75rem',
                    }}
                  >
                    {trendValue}
                  </Typography>
                )}
              </Box>
            )}
            {subtitle && (
              <Typography
                variant="caption"
                sx={{
                  color: '#6B7280',
                  fontSize: '0.75rem',
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default MetricCard;
